/**
 * LiveDispatchReel
 * ----------------
 * 30-second auto-looping animation that simulates the AskIsaac request
 * lifecycle live on the homepage hero. Six scenes, ~5s each, transitions
 * via Framer Motion. No video assets, no GIFs — pure React state.
 *
 * Each scene now carries a clinical backdrop photo (nurse / OR / supply
 * warehouse / hospital corridor / patient bay) so the reel reads as a
 * mini cinematic, not just a dashboard.
 *
 * Scenes:
 *   1 INPUT       — Nurse types backorder request in Telegram-style chat
 *   2 ANALYZE     — AskIsaac parses intent, identifies 3 candidate SKUs
 *   3 RFQ         — RFQ fans out to 4 suppliers in parallel
 *   4 QUOTES      — Quotes return; lowest compliant price selected
 *   5 DISPATCH    — Compliance auto-check + contractor dispatched
 *   6 CONFIRMED   — Order confirmed, audit trail written, ETA
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Cpu, CheckCircle2, Truck, ShieldCheck, FileCheck,
  Building2, Loader2, ArrowRight, Sparkles,
} from "lucide-react";

const SCENES = [
  { id: "input",     duration: 4500 },
  { id: "analyze",   duration: 4500 },
  { id: "rfq",       duration: 5000 },
  { id: "quotes",    duration: 5500 },
  { id: "dispatch",  duration: 5500 },
  { id: "confirmed", duration: 5000 },
];

// Clinical backdrop imagery — 1 short video hero-clip + 5 ken-burns photos
// focused on diverse medical staff, pediatric care, and elderly patient care.
// All sourced from Coverr (CC-permissive) & Pexels (royalty-free w/ model releases).
const BG = {
  // SCENE 1 — video: doctor & nurse walking a hospital corridor (~10s loop)
  input: {
    video:  "https://cdn.coverr.co/videos/coverr-a-doctor-and-a-nurse-walking-in-the-hospital-corridor-8988/1080p.mp4",
    poster: "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  // SCENE 2 — photo: diverse medical team huddle
  analyze:   "https://images.pexels.com/photos/7089336/pexels-photo-7089336.jpeg?auto=compress&cs=tinysrgb&w=1200",
  // SCENE 3 — photo: diverse medical staff walking a corridor
  rfq:       "https://images.pexels.com/photos/4173239/pexels-photo-4173239.jpeg?auto=compress&cs=tinysrgb&w=1200",
  // SCENE 4 — photo: pediatric clinician with a child
  quotes:    "https://images.pexels.com/photos/6749778/pexels-photo-6749778.jpeg?auto=compress&cs=tinysrgb&w=1200",
  // SCENE 5 — photo: hospital patient in bed with IV drip + monitor + visitor
  dispatch:  "https://images.pexels.com/photos/6129504/pexels-photo-6129504.jpeg?auto=compress&cs=tinysrgb&w=1200",
  // SCENE 6 — photo: patient on treadmill during stress test w/ medical staff
  confirmed: "https://images.pexels.com/photos/8460226/pexels-photo-8460226.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

const TOTAL_MS = SCENES.reduce((n, s) => n + s.duration, 0);

const fadeSlide = {
  initial: { opacity: 0, y: 12, scale: 0.99 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.35 } },
};

export default function LiveDispatchReel({ onPlayDemo }) {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [tick, setTick] = useState(0);

  // advance scene
  useEffect(() => {
    const t = setTimeout(
      () => setSceneIdx((sceneIdx + 1) % SCENES.length),
      SCENES[sceneIdx].duration,
    );
    return () => clearTimeout(t);
  }, [sceneIdx]);

  // global progress bar tick (1% per ~TOTAL_MS/100)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 100), TOTAL_MS / 100);
    return () => clearInterval(id);
  }, []);

  const scene = SCENES[sceneIdx].id;

  return (
    <div className="relative" data-testid="live-dispatch-reel">
      {/* Loop progress bar */}
      <div className="absolute -top-px left-0 right-0 h-[2px] bg-white/[0.04] overflow-hidden z-10">
        <motion.div
          key={tick}
          initial={{ width: `${tick}%` }}
          animate={{ width: `${(tick + 1)}%` }}
          transition={{ duration: TOTAL_MS / 100 / 1000, ease: "linear" }}
          className="h-full bg-gradient-to-r from-cyan-400 via-amber-300 to-cyan-400"
        />
      </div>

      {/* Stage — every scene paints its own backdrop, slow Ken-Burns zoom */}
      <div className="relative min-h-[340px] md:min-h-[360px] overflow-hidden">
        <AnimatePresence mode="wait">
          {scene === "input"     && <SceneInput     key="input" />}
          {scene === "analyze"   && <SceneAnalyze   key="analyze" />}
          {scene === "rfq"       && <SceneRFQ       key="rfq" />}
          {scene === "quotes"    && <SceneQuotes    key="quotes" />}
          {scene === "dispatch"  && <SceneDispatch  key="dispatch" />}
          {scene === "confirmed" && <SceneConfirmed key="confirmed" />}
        </AnimatePresence>
      </div>

      {/* Scene labels along the bottom */}
      <div className="grid grid-cols-6 gap-1 px-5 pb-2 pt-2">
        {SCENES.map((s, i) => (
          <div
            key={s.id}
            className={`text-[8px] font-bold tracking-wider text-center uppercase py-1 rounded transition-all duration-300 ${
              i === sceneIdx
                ? "bg-cyan-400/15 text-cyan-200 border border-cyan-400/30"
                : i < sceneIdx
                ? "text-emerald-400/60 border border-transparent"
                : "text-slate-600 border border-transparent"
            }`}
          >
            {s.id}
          </div>
        ))}
      </div>

      {/* Replay-as-full-case-study CTA */}
      {onPlayDemo && (
        <div className="px-5 pb-4 pt-1 border-t border-white/[0.04]">
          <button
            type="button"
            onClick={onPlayDemo}
            className="group w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.04] hover:bg-cyan-400/10 border border-white/[0.08] hover:border-cyan-400/40 text-cyan-100 hover:text-white text-[12px] font-semibold transition-all"
            data-testid="reel-replay-case-study"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-cyan-400/50 animate-ping" />
              <span className="relative w-2 h-2 rounded-full bg-cyan-300" />
            </span>
            Replay the full case study
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scene shell — handles photo backdrop + gradient overlay + Ken-Burns zoom
// ---------------------------------------------------------------------------
function SceneShell({ bg, children, tint = "from-[#0b1d3a]/55 via-[#0b1d3a]/25 to-[#061124]/75" }) {
  // bg can be:
  //   - string  → renders as an image with ken-burns motion
  //   - object  → { video, poster }: renders a muted looping video with the
  //     poster as an instant-paint fallback while the video buffers.
  const isVideo = bg && typeof bg === "object" && bg.video;
  return (
    <motion.div
      {...fadeSlide}
      className="absolute inset-0"
    >
      {isVideo ? (
        <>
          {/* Poster shown instantly while video buffers */}
          {bg.poster && (
            <img
              src={bg.poster}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "saturate(1.1) brightness(1.05)" }}
              loading="eager"
            />
          )}
          <video
            src={bg.video}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.86, filter: "saturate(1.1) brightness(1.05) contrast(1.05)" }}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
        </>
      ) : bg ? (
        <img
          src={bg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover animate-ken-burns-fast"
          style={{ opacity: 0.86, filter: "saturate(1.15) brightness(1.08) contrast(1.05)" }}
          loading="lazy"
        />
      ) : null}
      <div className={`absolute inset-0 bg-gradient-to-br ${tint}`} />
      <div className="relative h-full p-5 flex flex-col gap-3">
        {children}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// SCENE 1 · INPUT — Nurse types a backorder request
// ---------------------------------------------------------------------------
const SceneInput = () => (
  <SceneShell bg={BG.input}>
    <SceneHeader icon={Building2} label="Bayshore General · OR-3 · 06:18 CT" tone="amber" />
    <div className="rounded-xl bg-slate-900/70 border border-white/[0.08] p-3 backdrop-blur-md">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-200">Telegram → AskIsaac</span>
      </div>
      <TypingLine text="Hey — Ray-Cot patties on backorder again. Need 200 ct (1/2&quot; x 3&quot;) by 06:00 tomorrow for Dr. Patel's case. Help." />
      <div className="mt-3 flex items-center gap-2 text-[10px] text-cyan-300/90">
        <Send className="w-3 h-3" /> Sent · awaiting AI response…
      </div>
    </div>
  </SceneShell>
);

// ---------------------------------------------------------------------------
// SCENE 2 · ANALYZE — AskIsaac parses + identifies SKU candidates
// ---------------------------------------------------------------------------
const SceneAnalyze = () => (
  <SceneShell bg={BG.analyze}>
    <SceneHeader icon={Cpu} label="AskIsaac · Intent Analysis" tone="cyan" />
    <div className="rounded-xl bg-slate-900/70 border border-cyan-400/25 p-3 space-y-2 backdrop-blur-md">
      <PulseRow label="Intent" value="urgent_backorder · neuro_pattie" delay={0} />
      <PulseRow label="Facility" value="Bayshore General · Main Campus · OR-3" delay={0.3} />
      <PulseRow label="SKU match" value="3 candidates · Ray-Cot 1/2&quot;×3&quot;" delay={0.6} />
      <PulseRow label="Window" value="≤ 22h · same-day air available" delay={0.9} />
    </div>
    <div className="flex items-center gap-2 text-[10px] text-cyan-300/90">
      <Loader2 className="w-3 h-3 animate-spin" /> Routing RFQ to 4 suppliers…
    </div>
  </SceneShell>
);

// ---------------------------------------------------------------------------
// SCENE 3 · RFQ — Fan out to 4 suppliers
// ---------------------------------------------------------------------------
const SceneRFQ = () => {
  const suppliers = [
    { name: "American Surgical · MA",   border: "border-rose-400/30",   dot: "bg-rose-400" },
    { name: "Aspen Medical · KY",        border: "border-amber-400/30",  dot: "bg-amber-400" },
    { name: "BluBox · Direct Channel",   border: "border-violet-400/30", dot: "bg-violet-400" },
    { name: "Distributor #4 · Houston",  border: "border-cyan-400/30",   dot: "bg-cyan-400" },
  ];
  return (
    <SceneShell bg={BG.rfq}>
      <SceneHeader icon={Send} label="RFQ broadcast · 4 suppliers" tone="cyan" />
      <div className="space-y-1.5">
        {suppliers.map((s, i) => (
          <motion.div
            key={s.name}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 + i * 0.25, duration: 0.4 }}
            className={`flex items-center justify-between rounded-lg border bg-slate-900/65 backdrop-blur-md px-3 py-2 ${s.border}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot} animate-pulse`} />
              <span className="text-[11px] text-slate-100 font-sans truncate">{s.name}</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-300/90 shrink-0">
              <ArrowRight className="w-3 h-3" /> sent
            </div>
          </motion.div>
        ))}
      </div>
    </SceneShell>
  );
};

// ---------------------------------------------------------------------------
// SCENE 4 · QUOTES — Returns + lowest selected
// ---------------------------------------------------------------------------
const SceneQuotes = () => {
  const quotes = [
    { name: "American Surgical",  price: "$184.00", lead: "next-day",   best: true },
    { name: "Aspen Medical",      price: "$211.50", lead: "2-day",      best: false },
    { name: "BluBox",             price: "$199.20", lead: "next-day",   best: false },
    { name: "Distributor #4",     price: "$237.00", lead: "3-day",      best: false },
  ];
  return (
    <SceneShell bg={BG.quotes}>
      <SceneHeader icon={CheckCircle2} label="4 quotes returned · 31s" tone="emerald" />
      <div className="space-y-1.5">
        {quotes.map((q, i) => (
          <motion.div
            key={q.name}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + i * 0.18 }}
            className={`flex items-center justify-between rounded-lg px-3 py-2 border backdrop-blur-md ${
              q.best ? "bg-emerald-500/15 border-emerald-400/40 ring-1 ring-emerald-400/20" : "bg-slate-900/65 border-white/[0.08]"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {q.best && <Sparkles className="w-3 h-3 text-emerald-300 shrink-0" />}
              <span className={`text-[11px] truncate font-sans ${q.best ? "text-emerald-50 font-semibold" : "text-slate-200"}`}>
                {q.name}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[10px] text-slate-300 font-sans">{q.lead}</span>
              <span className={`text-xs font-bold tabular-nums ${q.best ? "text-emerald-100" : "text-slate-200"}`}>{q.price}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </SceneShell>
  );
};

// ---------------------------------------------------------------------------
// SCENE 5 · DISPATCH — Compliance + dispatch
// ---------------------------------------------------------------------------
const SceneDispatch = () => (
  <SceneShell bg={BG.dispatch}>
    <SceneHeader icon={ShieldCheck} label="Compliance · Dispatch · 0.8s" tone="amber" />
    <div className="grid grid-cols-2 gap-2">
      <ChecklistCard items={[
        { label: "FDA-cleared",        ok: true },
        { label: "AAMI ST108",         ok: true },
        { label: "Joint Commission",   ok: true },
        { label: "ICRA / Lot trace",   ok: true },
      ]} />
      <div className="rounded-lg border border-amber-400/30 bg-slate-900/65 backdrop-blur-md p-3">
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-4 h-4 text-amber-300" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-200">Dispatched</span>
        </div>
        <div className="text-[11px] text-slate-100 font-sans leading-snug">
          PO sent · carrier <strong className="text-white">FedEx Priority Overnight</strong>
        </div>
        <div className="text-[10px] text-slate-300 mt-1.5 font-sans">ETA Bayshore General · 04:42 CT</div>
      </div>
    </div>
  </SceneShell>
);

// ---------------------------------------------------------------------------
// SCENE 6 · CONFIRMED
// ---------------------------------------------------------------------------
const SceneConfirmed = () => (
  <SceneShell bg={BG.confirmed}>
    <SceneHeader icon={FileCheck} label="Order confirmed · audit logged" tone="emerald" />
    <div className="rounded-xl bg-slate-900/65 backdrop-blur-md border border-emerald-400/35 p-4 text-center">
      <CheckCircle2 className="w-9 h-9 text-emerald-300 mx-auto mb-2" />
      <div className="font-display text-white text-base font-semibold">Closed in <span className="text-emerald-300 tabular-nums">42s</span></div>
      <div className="text-[11px] text-slate-200 mt-1 font-sans">200 ct · Ray-Cot 1/2&quot;×3&quot; · $184.00 · arriving 04:42 CT</div>
      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] font-bold text-emerald-100 uppercase tracking-wider">Audit · 12 events written</span>
      </div>
    </div>
    <div className="text-center text-[10px] text-slate-400 font-sans">Loop restarts in a moment…</div>
  </SceneShell>
);

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------
const TONE_MAP = {
  amber:   { bg: "bg-amber-500/15",   text: "text-amber-200",   ring: "border-amber-400/30" },
  cyan:    { bg: "bg-cyan-500/15",    text: "text-cyan-200",    ring: "border-cyan-400/30" },
  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-200", ring: "border-emerald-400/30" },
};

function SceneHeader({ icon: Icon, label, tone = "cyan" }) {
  const t = TONE_MAP[tone];
  return (
    <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border ${t.bg} ${t.ring} backdrop-blur-md w-fit`}>
      <Icon className={`w-3 h-3 ${t.text}`} />
      <span className={`text-[10px] font-bold uppercase tracking-wider ${t.text}`}>{label}</span>
    </div>
  );
}

function PulseRow({ label, value, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="flex items-center justify-between text-[11px] font-sans"
    >
      <span className="text-slate-400 uppercase tracking-wider text-[9px] font-bold">{label}</span>
      <span className="text-slate-100">{value}</span>
    </motion.div>
  );
}

function ChecklistCard({ items }) {
  return (
    <div className="rounded-lg border border-emerald-400/30 bg-slate-900/65 backdrop-blur-md p-3">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-emerald-300" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-200">Compliance</span>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.15 }}
            className="flex items-center gap-1.5 text-[11px] text-slate-100 font-sans"
          >
            <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
            <span>{it.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function TypingLine({ text }) {
  // Character-by-character reveal
  const [shown, setShown] = useState("");
  useEffect(() => {
    setShown("");
    let i = 0;
    const id = setInterval(() => {
      i++;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [text]);
  return (
    <p className="text-[12px] text-slate-50 font-sans leading-relaxed">
      {shown}
      <span className="inline-block w-1.5 h-3 bg-amber-300 ml-0.5 align-middle animate-pulse" />
    </p>
  );
}
